import { Modal } from '@material-ui/core'
import React, { useState } from 'react'
import axios from '../../axios'
import { ENDPOINTS } from '../../constants'
import { sendToExtension } from '../../../utils/extensionHelper'
import BusinessFlow from './BusinessFlow'
import { REVIEW_NOTICE, Segment, SEGMENTS } from './content'
import InfluencerFlow from './InfluencerFlow'
import MarketerFlow from './MarketerFlow'
import { styles } from './styles'

interface ShareLinkWizardProps {
  open: boolean
  onClose: () => void
  /** Creates the short link for the pending URL; returns the created link data or throws. */
  createLink: () => Promise<any>
  /** Called after the declaration is persisted so the parent can update appState. */
  onDeclared: (update: {
    shareLinkSegment: Segment
    shareLinkDeclaredAt: string
    shareLinkReviewStatus: string
  }) => void
  /** Called with the created short-link data so the parent page can reflect it. */
  onLinkCreated: (data: any) => void
}

type Step = 'who' | 'flow' | 'success'

const SEGMENT_TITLE: Record<Segment, string> = {
  influencer: 'Influencer / creator',
  digital_marketer: 'Digital marketer',
  business: 'Business',
}

const ShareLinkWizard = ({
  open,
  onClose,
  createLink,
  onDeclared,
  onLinkCreated,
}: ShareLinkWizardProps) => {
  const classes = styles()
  const [step, setStep] = useState<Step>('who')
  const [segment, setSegment] = useState<Segment | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [result, setResult] = useState<any>(null)
  const [declared, setDeclared] = useState(false)

  const reset = () => {
    setStep('who')
    setSegment(null)
    setSubmitting(false)
    setError('')
    setResult(null)
    setDeclared(false)
  }

  const handleClose = () => {
    onClose()
    reset()
  }

  const handleSelect = (id: Segment) => {
    setSegment(id)
    setError('')
    setStep('flow')
  }

  const handleFlowSubmit = async (
    answers: Record<string, any>,
    consents: Record<string, boolean>
  ) => {
    if (!segment) return
    setSubmitting(true)
    setError('')
    try {
      if (!declared) {
        try {
          await axios.post(ENDPOINTS.shareLinkDeclaration, {
            segment,
            answers,
            consents,
          })
        } catch (declarationErr) {
          // Stale local state (e.g. declared on another tab/session) — the
          // backend is the source of truth, so treat "already declared" as
          // success and continue to link creation instead of dead-ending.
          if (declarationErr?.response?.status !== 409) {
            throw declarationErr
          }
        }
        const declaredAt = new Date().toISOString()
        setDeclared(true)
        onDeclared({
          shareLinkSegment: segment,
          shareLinkDeclaredAt: declaredAt,
          shareLinkReviewStatus: 'pending',
        })
        sendToExtension('CHR_DECLARATION_COMPLETE', {
          shareLinkDeclaredAt: declaredAt,
        })
      }
      const data = await createLink()
      setResult(data)
      onLinkCreated(data)
      setStep('success')
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          'Something went wrong, please try again.'
      )
    } finally {
      setSubmitting(false)
    }
  }

  const copyLink = () => {
    if (result?.shortUrl && navigator?.clipboard) {
      navigator.clipboard.writeText(result.shortUrl)
    }
  }

  const backToChooser = () => {
    setStep('who')
    setError('')
  }

  const renderFlow = () => {
    const flowProps = {
      submitting,
      error,
      onBack: backToChooser,
      onSubmit: handleFlowSubmit,
    }
    if (segment === 'influencer') return <InfluencerFlow {...flowProps} />
    if (segment === 'digital_marketer') return <MarketerFlow {...flowProps} />
    if (segment === 'business') return <BusinessFlow {...flowProps} />
    return null
  }

  const eyebrow =
    step === 'who' ? 'Create share link' : segment ? SEGMENT_TITLE[segment] : ''
  const heading =
    step === 'who'
      ? 'Who are you?'
      : step === 'success'
      ? "You're all set"
      : segment === 'influencer'
      ? 'Where do you create?'
      : segment === 'digital_marketer'
      ? 'Compliance declaration'
      : 'About your business'

  const notice = segment ? REVIEW_NOTICE[segment] : null

  return (
    <Modal open={open} onClose={handleClose}>
      <div className={classes.paper}>
        <div className={classes.header}>
          <div>
            <p className={classes.eyebrow}>{eyebrow}</p>
            <h3 className={classes.title}>{heading}</h3>
          </div>
          <button type="button" className={classes.close} onClick={handleClose}>
            ×
          </button>
        </div>

        {step === 'who' && (
          <div>
            <p className={classes.intro}>
              Select the option that best describes you so we can tailor your
              experience.
            </p>
            {SEGMENTS.map(s => (
              <button
                key={s.id}
                type="button"
                className={classes.roleCard}
                onClick={() => handleSelect(s.id)}
              >
                <div>
                  <p className={classes.roleTitle}>{s.title}</p>
                  <p className={classes.roleDesc}>{s.description}</p>
                </div>
              </button>
            ))}
          </div>
        )}

        {step === 'flow' && renderFlow()}

        {step === 'success' && notice && (
          <div>
            <h3 className={classes.successHeading}>{notice.heading}</h3>
            <div className={classes.reviewBox}>
              <p className={classes.reviewText}>{notice.body}</p>
            </div>
            <div style={{ marginBottom: 12 }}>
              {notice.bullets.map((b, i) => (
                <p
                  key={i}
                  style={{ fontSize: 12, color: '#666', margin: '0 0 6px' }}
                >
                  {'✓ '}
                  {b}
                </p>
              ))}
            </div>
            {result?.shortUrl && (
              <div className={classes.linkBox}>
                <span className={classes.linkText}>{result.shortUrl}</span>
                <button
                  type="button"
                  className={classes.copyBtn}
                  onClick={copyLink}
                >
                  Copy
                </button>
              </div>
            )}
            <button
              type="button"
              className={classes.primaryBtn}
              onClick={handleClose}
            >
              Got it, I'll start sharing
            </button>
          </div>
        )}
      </div>
    </Modal>
  )
}

export default ShareLinkWizard
